import { useQuery } from '@tanstack/react-query';
import { Form } from 'antd';
import { useEffect, useState } from 'react';
import { useForm } from '../components/Form/context';
import { calculatePaymentMethodDiscount, moneyMask } from '../components/Form/helper';
import { checkoutService } from '../services/CheckoutService';
import { useDebounce } from './useDebounce';

export const useInstallments = (name: (string | number)[]) => {
  name = name ?? [];
  const [firstValue, setFirstValue] = useState();
  const [valueDirty, setValueDirty] = useState(false);

  const parentName = name.slice(0, -1);
  const { checkout, form, isRecurrence, getTotal, items, isForeigner, isOCB, setInstallments } = useForm();
  const payments = Form.useWatch(['payments'], form);

  const value = Form.useWatch(['payments', ...parentName, 'value'], form);

  const hasEnrollmentFee = items.some((item) => item.type === 'enrollment_fee');
  const firstItem = items.find((item) => ['main', 'cart'].includes(item.type));
  const isOpenValue = firstItem?.config?.isOpenValue;

  const valueDebounced = useDebounce(value, 500);

  const total = getTotal();

  // Desconto do cartão (preço "à vista" até o teto de parcelas). As parcelas são geradas sobre a
  // base (sem o desconto) e o desconto é embutido por-parcela no backend — senão entraria em dobro.
  const discounts = checkout?.paymentMethodDiscounts ?? [];
  const cardDiscount = discounts.find((d) => d.paymentMethod === 'credit_card');
  const hasCardDiscount = !!cardDiscount && cardDiscount.value > 0;
  const cardDiscountAmount = hasCardDiscount
    ? calculatePaymentMethodDiscount('credit_card', 1, items, discounts)
    : 0;
  const cardDiscountMaxInstallments = cardDiscount?.maxInstallments ?? checkout?.maxInstallments;
  const applyCardDiscount = hasCardDiscount && cardDiscountAmount > 0;

  let { data, isLoading } = useQuery({
    queryKey: ['installments', ...name, valueDebounced, cardDiscountAmount, cardDiscountMaxInstallments],
    queryFn: () =>
      checkoutService.getInstallments({
        total: applyCardDiscount ? total / 100.0 : moneyMask.clean(value),
        maxInstallments: checkout?.maxInstallments,
        installmentFee: checkout?.installmentFee,
        freeInstallments: checkout?.freeInstallments,
        installmentGeneratorHash: checkout?.installmentGeneratorHash,
        ...(applyCardDiscount
          ? {
              discountAmount: cardDiscountAmount / 100.0,
              discountMaxInstallments: cardDiscountMaxInstallments,
            }
          : {}),
      }),
    enabled:
      !!checkout &&
      !!value &&
      !!checkout.installmentGeneratorHash &&
      valueDirty &&
      moneyMask.clean(value) * 100 <= total,
  });

  data = valueDirty ? data : isOpenValue ? [] : checkout?.installments;

  useEffect(() => {
    if (!data) return;

    setInstallments(data);

    const baseName = ['payments', ...(name as string[])];

    const currentInstallment = form.getFieldValue([...baseName, 'installments']);
    if (!currentInstallment) {
      // Parcela pré-selecionada configurada no checkout. Vale no avulso e na adesão (que é uma
      // cobrança única financiável); em recorrência pura não há parcelamento a pré-selecionar. Se
      // não existir na lista, cai no padrão: primeira (recorrência) ou última/maior.
      const honorsDefault = !isRecurrence || hasEnrollmentFee;
      const preferred =
        honorsDefault && checkout?.defaultInstallments
          ? data.find((item: any) => item.installment == checkout.defaultInstallments)
          : undefined;
      const installment = preferred ?? data[isRecurrence && !hasEnrollmentFee ? 0 : data.length - 1];
      if (!installment) return;

      form.setFieldValue([...baseName, 'installments'], installment?.installment);
      form.setFieldValue([...baseName, 'installmentValue'], installment?.value);
      form.setFieldValue([...baseName, 'installmentHash'], installment?.hash);

      return;
    }

    let installment = data.find((installment: any) => installment.installment == currentInstallment);
    if (!installment) {
      installment = data[isRecurrence ? 0 : data.length - 1];
    }

    form.setFieldValue([...baseName, 'installments'], installment?.installment);
    form.setFieldValue([...baseName, 'installmentValue'], installment?.value);
    form.setFieldValue([...baseName, 'installmentHash'], installment?.hash);
  }, [data, isForeigner, isOCB]);

  useEffect(() => {
    if (!value) return;

    setFirstValue(value);
  }, [value]);

  useEffect(() => {
    if (!value) return;

    if (isOpenValue) {
      setValueDirty(true);
      return;
    }

    if (!firstValue) return;

    if (firstValue == value && payments.length == 1) return;

    setValueDirty(true);
  }, [value, firstValue, payments, isOpenValue]);

  useEffect(() => {
    const forceNewInstallments = form.getFieldValue(['payments', ...parentName, 'forceNewInstallments']);
    if (forceNewInstallments && !valueDirty) {
      setValueDirty(true);
    }
  }, [parentName, valueDirty, payments]);

  const hasFee = (checkout?.installmentFee ?? 0) > 0;
  const freeInstallments = checkout?.freeInstallments ?? 1;

  const options = data?.map((installment: any) => {
    const n = installment.installment;
    // Parcelas 2x..freeInstallments são explicitamente "sem juros"; acima disso mantém o `*`
    // (nota de rodapé de juros). 1x é à vista, não precisa de rótulo.
    const suffix = n > 1 && n <= freeInstallments ? ' sem juros' : hasFee && n != 1 ? '*' : '';

    return {
      label: `${n}x de ${new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(installment.value)}${suffix}`,
      value: n,
    };
  });

  return {
    options,
    isLoading,
    data,
  };
};
